import config from '@/config';

/**
 * Route protection is a property of the **(route, template, tenant auth
 * config)** triple — not of the route.
 *
 * This is the correction to the route catalog's original shape, in two steps.
 *
 * **1. Protection isn't a property of the route.** The catalog declares a
 * canonical route's *identity* — path, parent, `validateSearch` — exactly
 * once, which is right: `issues/$id` is the same URL with the same params in
 * every template that has it. But "is it protected?" is not part of that
 * identity. `sections` leaves the issue route open, `magshop` puts a Cognito
 * guard in front of it, `padi` wraps it in a paywall countdown. Same route,
 * three answers. Baking one answer into the catalog entry forced every
 * template that disagreed to re-declare the whole route in its own `custom`
 * block — which is how `issues/$id` ended up declared six times across the
 * tree, each copy re-stating the path, the parent and the search schema just
 * to change the guard. That's exactly the drift the catalog exists to
 * prevent, reopened for the most important route in the app.
 *
 * **2. It isn't a property of the template either.** One template serves many
 * tenants, and `config.app.auth` differs between them: the same `basic` build
 * runs against Cognito for one publisher and token auth for another. So a
 * template declares *which routes it wants protected* and *which guard applies
 * to which auth type*; the tenant's config picks.
 *
 * That per-auth-type mapping is not a hypothetical nicety — the two Cognito
 * templates already disagree about it, and the disagreement used to be
 * invisible. `basic`/`magazine`'s adapter is built with
 * `requireCognitoAuthType: true`, so its guard self-gates to Cognito tenants
 * only; `magshop`'s is built with `requireCognitoAuthType: false`, so its
 * guard applies to *every* enabled auth type. Both used to be spelled
 * identically at the router (`beforeLoad: () => ensureAuthenticated(...)`),
 * with the real difference buried in a flag three files away. Here it's
 * declared: `{ cognito: … }` vs `{ '*': … }`.
 */

/** The auth types a tenant's `config.app.auth` can declare. */
export type TenantAuthType =
	| 'cognito'
	| 'token'
	| 'login'
	| 'jwt'
	| 'date-token'
	| 'oauth';

/**
 * The hook a route runs during render. It returns the route's log-out handler
 * (or `undefined` when the reader isn't authenticated), which is why it has to
 * be a *hook* — every real implementation subscribes to auth state.
 *
 * Exactly one is called, unconditionally, on every render of the route's
 * component; templates that supply none fall back to the route's own default
 * gate. Only the implementation varies, and it's fixed at build time, so
 * React's hook order is stable.
 *
 * **Deliberately not keyed by auth type.** It only decides whether to offer a
 * log-out affordance, and `AUTH_KEY` is set by every mechanism that logs a
 * reader in — Cognito's `saveTokens`, `direct-auth.ts`'s login/JWT flow, and
 * several templates' own home pages. Gating it on `type === 'cognito'` would
 * take the log-out button away from a `basic` tenant running `login` auth,
 * which works today.
 */
export type RouteGateHook = () => ((url?: string) => void) | undefined;

/**
 * What a guard is handed. Kept to the fields a guard actually needs rather
 * than TanStack's full `beforeLoad` context, whose type is parented to one
 * specific route's generics and so doesn't match across the different parent
 * routes this map has to cover.
 */
export interface RouteGuardContext {
	params: Record<string, string>;
	/**
	 * Added for `token-auth-guard.ts`'s `tokenAuthGuard`, the first guard
	 * that needs to read a search param (`?token=`) rather than just
	 * `params`. Untyped on purpose -- a guard shared across every parent
	 * route in the catalog can't assume one route's `validateSearch` shape.
	 */
	search: Record<string, unknown>;
	/** The current route's resolved pathname, for a guard that redirects back to itself (e.g. to strip a one-time query param) rather than always to a fixed destination. */
	pathname: string;
}

export interface RouteProtection {
	/**
	 * Runs before the route loads. Throw a `redirect()` from here to block —
	 * that's what makes this a real guard rather than a render-time bounce.
	 */
	beforeLoad?: RouteGuard;
	/**
	 * Merged into the route's context (and inherited by its children), so the
	 * route's lazily-loaded component can reach template-specific auth wiring
	 * it cannot import directly. A `route.lazy()` module is shared by all ten
	 * templates and can't close over one template's adapter.
	 */
	provide?: Record<string, unknown>;
}

/**
 * What a template declares per route.
 *
 * `'auth'` — "protect this with whatever `config.app.auth` says". The normal
 * case, and the one that lets a single template serve tenants on different
 * IdPs.
 *
 * A literal `RouteProtection` — an escape hatch for a template whose gate
 * genuinely isn't the tenant's auth mechanism (`padi`'s paywall countdown is
 * the real example: it's a subscription timer, not an identity check).
 */
export type RouteProtectionSpec = 'auth' | RouteProtection;

export type RouteGuard = (
	context: RouteGuardContext,
) => unknown | Promise<unknown>;

/** The auth wiring a template offers. `config.app.auth.type` picks from it. */
export interface TemplateAuthWiring {
	/**
	 * Guards keyed by the tenant's auth type. `'*'` applies to any type and is
	 * the fallback when the specific type has no entry.
	 *
	 * Each guard may still self-gate internally (Cognito's
	 * `ensureAuthenticated` checks `config.app.auth.enabled` before doing
	 * anything). That's intentional overlap, not redundancy: this map decides
	 * *which* guard is relevant to a tenant, the guard itself decides whether
	 * there's anything to enforce.
	 */
	guards?: Partial<Record<TenantAuthType | '*', RouteGuard>>;
	/** See `RouteGateHook` — supplied regardless of auth type. */
	useRouteGate?: RouteGateHook;
}

/**
 * Resolves what a template asked for against what the tenant is configured
 * with. `undefined` means "no route-level protection" — for `issue`/
 * `issueArticle` specifically, the catalog still falls back to its own
 * default `beforeLoad` guard (`route-catalog.tsx`'s
 * `DEFAULT_ISSUE_PROTECTION`, `token-auth-guard.ts`'s `tokenAuthGuard`,
 * itself a no-op unless `config.app.auth.type === 'token'`) rather than
 * leaving the route genuinely unguarded.
 */
export function resolveProtection(
	spec: RouteProtectionSpec | undefined,
	wiring: TemplateAuthWiring | undefined,
): RouteProtection | undefined {
	if (!spec) return undefined;
	if (spec !== 'auth') return spec;
	if (!wiring) return undefined;

	const authType = config?.app?.auth?.type as TenantAuthType | undefined;
	const beforeLoad =
		(authType ? wiring.guards?.[authType] : undefined) ??
		wiring.guards?.['*'];
	const provide = wiring.useRouteGate
		? { useRouteGate: wiring.useRouteGate }
		: undefined;

	if (!beforeLoad && !provide) return undefined;
	return { beforeLoad, provide };
}
